import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  Loader2Icon,
  MailCheckIcon,
  RefreshCcwIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { AppSidebarLayout } from '@/components/app-sidebar';
import { SettingsPage } from '@/components/settings-page';
import { DesignSystemPage } from '@/components/design-system-page';
import { AuthShell } from '@/components/auth-shell';
import { ForgotPasswordForm } from '@/components/forgot-password-form';
import { LoginForm } from '@/components/login-form';
import { ResetPasswordForm } from '@/components/reset-password-form';
import { SetPasswordForm } from '@/components/set-password-form';
import { SignupForm } from '@/components/signup-form';
import { CreateUserPage } from '@/components/create-user-page';
import { UsersManagementPage } from '@/components/users-management-page';
import { ChatPage } from '@/features/chat';
import { AnimatedOutlet } from '@/components/animated-outlet';
import { DocumentsPage } from '@/features/documents/components/DocumentsPage';
import { AdminUserQuotaPage } from '@/features/quota';
import { AdminCompaniesPage } from '@/features/company/AdminCompaniesPage';
import { AdminCompanyUsagePage } from '@/features/usage/components/AdminCompanyUsagePage';
import { CompanyMembersPage } from '@/features/company/CompanyMembersPage';
import { CompanyMemberCreatePage } from '@/features/company/CompanyMemberCreatePage';
import { activeCompanyMembersScope } from '@/features/company/company-api';
import { useOrganizationAccess } from '@/features/organization/useOrganizationAccess';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { apiBaseUrl, authClient, getBrowserUrl, sendVerificationEmail } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { SentryUserScope } from '@/lib/sentry';
import './App.css';

const DatasourcePage = lazy(() => import('@/features/datasource').then(
  (module) => ({ default: module.DatasourcePage }),
));


const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
type SessionData = NonNullable<ReturnType<typeof authClient.useSession>['data']>;
type SessionUserWithRole = SessionData['user'] & { role?: string | null };

function FullScreenLoader() {
  return (
    <main className="route-transition-enter flex min-h-screen items-center justify-center bg-background px-6">
      <Loader2Icon className="size-8 animate-spin text-primary" />
    </main>
  );
}

function DelayedSessionLoader({ isPending }: { isPending: boolean }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isPending) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- hides the delayed loader immediately when the external auth state settles.
      setIsVisible(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsVisible(true);
    }, 180);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPending]);

  if (!isPending || !isVisible) {
    return null;
  }

  return <FullScreenLoader />;
}

function PublicOnlyLayout() {
  const location = useLocation();
  const sessionState = authClient.useSession();
  const canStayWhileAuthenticated = location.pathname === '/auth/verify-email' || location.pathname === '/auth/set-password';

  if (sessionState.isPending) {
    return <DelayedSessionLoader isPending={sessionState.isPending} />;
  }

  if (sessionState.data && !canStayWhileAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <AnimatedOutlet />;
}

function getUserRole(session: SessionData | null | undefined) {
  return ((session?.user as SessionUserWithRole | undefined)?.role ?? 'user').toLowerCase();
}

function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionState = authClient.useSession();

  if (sessionState.isPending) {
    return <DelayedSessionLoader isPending={sessionState.isPending} />;
  }

  if (!sessionState.data) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  async function handleSignOut() {
    const result = await authClient.signOut();

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    await sessionState.refetch();
    toast.success('Session fermée.');
    navigate('/auth/login', { replace: true });
  }

  return (
    <AppSidebarLayout onSignOut={handleSignOut} session={sessionState.data}>
      <div className="flex min-w-0 w-full flex-1 flex-col">
        <AnimatedOutlet context={{ session: sessionState.data, sessionState }} />
      </div>
    </AppSidebarLayout>
  );
}

function LoginPage() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/';

  useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      toast.success('Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.');
    }
  }, [searchParams]);

  return (
    <AuthShell
      eyebrow=""
      title=""
      description=""
    >
      <LoginForm redirectTo={redirectTo} />
    </AuthShell>
  );
}

function RegisterPage() {
  return (
    <AuthShell
      badge="Inscription"
      title="Créez votre compte Inersia."
      description="Activez votre accès à votre agent conversationnel souverain : interrogez vos documents et collaborez avec votre équipe."
    >
      <SignupForm />
    </AuthShell>
  );
}

function ForgotPasswordPage() {
  return (
    <AuthShell
      badge=""
      eyebrow=""
      title=""
      description=""
    >
      <ForgotPasswordForm />
    </AuthShell>
  );
}

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const isValidated = searchParams.get('validated') === '1';
  const shouldPrevalidateToken = Boolean(token && !isValidated && !errorCode);
  const resetValidationCallbackUrl = useMemo(() => getBrowserUrl('/auth/reset-password?validated=1'), []);

  useEffect(() => {
    if (!shouldPrevalidateToken || !token) {
      return undefined;
    }

    const backendValidationUrl = new URL(`/api/auth/reset-password/${token}`, apiBaseUrl);
    backendValidationUrl.searchParams.set('callbackURL', resetValidationCallbackUrl);

    const timeoutId = window.setTimeout(() => {
      window.location.replace(backendValidationUrl.toString());
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [resetValidationCallbackUrl, shouldPrevalidateToken, token]);

  if (shouldPrevalidateToken) {
    return (
      <AuthShell
        badge="Validation Reset"
        title="Validation du lien en cours."
        description="Nous vérifions votre lien de réinitialisation avant d’afficher le formulaire."
      >
        <div data-testid="reset-password-prevalidation-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
            <Loader2Icon className="size-7 animate-spin" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Validation du lien en cours</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              Nous vérifions votre lien de réinitialisation avant d’afficher le formulaire.
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (errorCode) {
    return (
      <AuthShell
        badge="Lien Invalide"
        title="Ce lien de réinitialisation est invalide."
        description="Le token a expiré, est incorrect ou a déjà été utilisé."
      >
        <div data-testid="reset-password-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Lien de réinitialisation invalide</h1>
            <p data-testid="reset-password-error-message" className="text-sm leading-6 text-muted-foreground">
              {getAuthErrorMessage({ code: errorCode })}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button asChild>
              <a href="/auth/forgot-password">Demander un nouveau lien</a>
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      badge="Nouveau Mot De Passe"
      title="Termine la réinitialisation depuis le lien reçu par email."
      description="Le lien a déjà été prévalidé. Vous pouvez maintenant choisir un nouveau mot de passe."
    >
      <ResetPasswordForm token={isValidated ? token : null} />
    </AuthShell>
  );
}

function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionState = authClient.useSession();
  const hasScheduledRedirect = useRef(false);
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const email = searchParams.get('email') ?? sessionState.data?.user.email ?? '';
  const mode = searchParams.get('mode');
  const verificationCallbackUrl = useMemo(() => {
    const query = new URLSearchParams();

    if (email) {
      query.set('email', email);
    }

    if (mode) {
      query.set('mode', mode);
    }

    const callbackPath = query.size > 0 ? `/auth/verify-email?${query.toString()}` : '/auth/verify-email';

    return getBrowserUrl(callbackPath);
  }, [email, mode]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const backendVerificationUrl = new URL('/api/auth/verify-email', apiBaseUrl);
    backendVerificationUrl.searchParams.set('token', token);
    backendVerificationUrl.searchParams.set('callbackURL', verificationCallbackUrl);

    const timeoutId = window.setTimeout(() => {
      window.location.replace(backendVerificationUrl.toString());
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [token, verificationCallbackUrl]);

  useEffect(() => {
    if (mode === 'set-password' || token || !sessionState.data?.user.emailVerified || hasScheduledRedirect.current) {
      return undefined;
    }

    hasScheduledRedirect.current = true;
    toast.success('Email vérifié. Redirection vers l application.');

    const timeoutId = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mode, navigate, sessionState.data, token]);

  async function handleResendEmail() {
    if (!email) {
      toast.error('Aucun email disponible pour renvoyer le lien.');
      return;
    }

    const query = new URLSearchParams();

    if (email) {
      query.set('email', email);
    }

    if (mode) {
      query.set('mode', mode);
    }

    const callbackPath = query.size > 0 ? `/auth/verify-email?${query.toString()}` : '/auth/verify-email';
    const result = await sendVerificationEmail(email, getBrowserUrl(callbackPath));

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien de confirmation a été envoyé.');
  }

  return (
    <AuthShell
      badge="Vérification Email"
      title="Confirmez votre adresse e-mail avant la première connexion."
      description="Le clic arrive d abord sur cette page frontend, matérialise la validation en cours puis délègue la vérification réelle au backend Better Auth."
    >
      {token ? (
        <div data-testid="verify-email-prevalidation-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
            <Loader2Icon className="size-7 animate-spin" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Activation du compte en cours</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Nous vérifions actuellement ${email} et préparons votre session.`
                : 'Nous vérifions actuellement votre adresse e-mail et préparons votre session.'}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Vous allez être redirigé automatiquement dans un instant.
            </p>
          </div>
        </div>
      ) : errorCode ? (
        <div data-testid="verify-email-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Le lien de confirmation a un problème</h1>
            <p data-testid="verify-email-error-message" className="text-sm leading-6 text-muted-foreground">
              {getAuthErrorMessage({ code: errorCode })}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Vous pouvez demander un nouveau lien de confirmation si nécessaire.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      ) : sessionState.data?.user.emailVerified ? (
        <div data-testid="verify-email-success-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Compte activé</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {mode === 'set-password'
                ? 'Votre adresse e-mail est confirmée. Vous pouvez maintenant définir votre mot de passe.'
                : 'Votre adresse e-mail est confirmée. Vous pouvez maintenant ouvrir l’application.'}
            </p>
          </div>
          {mode === 'set-password' ? (
            <div className="flex flex-col gap-3">
              <Button asChild>
                <a href="/auth/set-password">Définir mon mot de passe</a>
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <Button asChild>
                <a href="/">Ouvrir l application</a>
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div data-testid="verify-email-idle-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Vérifiez votre adresse e-mail</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Un lien de confirmation a été envoyé à ${email}.`
                : 'Un lien de confirmation vous a été envoyé par e-mail.'}
            </p>
            <p className="text-sm leading-6 text-muted-foreground">
              Cliquez sur le lien reçu par e-mail pour activer votre compte ; cette page vous redirigera ensuite automatiquement.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      )}
    </AuthShell>
  );
}

function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const sessionState = authClient.useSession();
  const token = searchParams.get('token');
  const errorCode = searchParams.get('error');
  const email = searchParams.get('email') ?? sessionState.data?.user.email ?? '';
  const verificationCallbackUrl = useMemo(() => {
    const query = new URLSearchParams();

    if (email) {
      query.set('email', email);
    }

    const callbackPath = query.size > 0 ? `/auth/set-password?${query.toString()}` : '/auth/set-password';
    return getBrowserUrl(callbackPath);
  }, [email]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const backendVerificationUrl = new URL('/api/auth/verify-email', apiBaseUrl);
    backendVerificationUrl.searchParams.set('token', token);
    backendVerificationUrl.searchParams.set('callbackURL', verificationCallbackUrl);

    const timeoutId = window.setTimeout(() => {
      window.location.replace(backendVerificationUrl.toString());
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [token, verificationCallbackUrl]);

  async function handleResendEmail() {
    if (!email) {
      toast.error('Aucun email disponible pour renvoyer le lien.');
      return;
    }

    const result = await sendVerificationEmail(
      email,
      getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(email)}`),
    );

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien d activation a été envoyé.');
  }

  if (token) {
    return (
      <AuthShell
        badge="Activation Compte"
        title="Validation de l invitation en cours."
        description="Le lien vérifie d abord l email côté backend Better Auth, puis ouvre la session pour permettre la définition du mot de passe."
      >
        <div data-testid="set-password-prevalidation-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">
            <Loader2Icon className="size-7 animate-spin" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Activation du compte en cours</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              Nous confirmons votre adresse e-mail avant d’afficher l’étape de définition du mot de passe.
            </p>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (errorCode) {
    return (
      <AuthShell
        badge="Lien Invalide"
        title="Le lien d invitation ne peut pas être utilisé."
        description="Le token a expiré, est invalide ou a déjà été consommé."
      >
        <div data-testid="set-password-error-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Lien d activation invalide</h1>
            <p className="text-sm leading-6 text-muted-foreground">{getAuthErrorMessage({ code: errorCode })}</p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  if (!sessionState.data) {
    return (
      <AuthShell
        badge="Invitation"
        title="Consultez votre e-mail pour activer le compte."
        description="L activation de l email ouvre ensuite directement l étape de création du mot de passe."
      >
        <div data-testid="set-password-idle-state" className="space-y-6 text-center">
          <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <MailCheckIcon className="size-7" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">Activez d’abord votre compte</h1>
            <p className="text-sm leading-6 text-muted-foreground">
              {email
                ? `Un e-mail d’activation a été envoyé à ${email}.`
                : 'Un e-mail d’activation vous a été envoyé.'}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleResendEmail()}>
              <RefreshCcwIcon />
              Renvoyer le lien
            </Button>
            <Button asChild variant="outline">
              <a href="/auth/login">Retour à la connexion</a>
            </Button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title=""
      description=""
    >
      <SetPasswordForm />
    </AuthShell>
  );
}

function HomePage() {
  const sessionState = authClient.useSession();

  if (sessionState.isPending) {
    return <DelayedSessionLoader isPending={sessionState.isPending} />;
  }

  // Authenticated visitors land in the product (keeps the post-login redirect to `/` working).
  if (sessionState.data) {
    return <Navigate to="/chat" replace />;
  }

  // Landing page temporairement désactivée : les visiteurs non connectés sont
  // redirigés vers la connexion. Restaurer <LandingPage /> pour la réactiver.
  return <Navigate to="/auth/login" replace />;
}

function AdminUsersPage() {
  const sessionState = authClient.useSession();
  const session = sessionState.data;

  if (!session) {
    return null;
  }

  if (getUserRole(session) !== 'admin') {
    return (
      <section className="rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
        <div className="max-w-xl space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase dark:bg-amber-500/15 dark:text-amber-300">
            <ShieldCheckIcon className="size-3.5" />
            Accès restreint
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Zone réservée aux administrateurs</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            La gestion des utilisateurs est limitée aux comptes admin. Le backend Better Auth appliquera de toute façon
            la même contrainte côté API.
          </p>
        </div>
      </section>
    );
  }

  return <UsersManagementPage session={session} />;
}

function AdminCreateUserPage() {
  const sessionState = authClient.useSession();
  const session = sessionState.data;

  if (!session) {
    return null;
  }

  if (getUserRole(session) !== 'admin') {
    return (
      <section className="rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
        <div className="max-w-xl space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase dark:bg-amber-500/15 dark:text-amber-300">
            <ShieldCheckIcon className="size-3.5" />
            Accès restreint
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Zone réservée aux administrateurs</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            La création de comptes est limitée aux comptes admin. Le backend Better Auth appliquera de toute façon la
            même contrainte côté API.
          </p>
        </div>
      </section>
    );
  }

  return <CreateUserPage />;
}

function AdminUserQuotaRoutePage() {
  const sessionState = authClient.useSession();
  const session = sessionState.data;

  if (!session) {
    return null;
  }

  if (getUserRole(session) !== 'admin') {
    return (
      <section className="rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
        <div className="max-w-xl space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase dark:bg-amber-500/15 dark:text-amber-300">
            <ShieldCheckIcon className="size-3.5" />
            Accès restreint
          </div>
          <h1 className="text-2xl font-semibold text-foreground">Zone réservée aux administrateurs</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            La gestion des quotas est limitée aux comptes admin.
          </p>
        </div>
      </section>
    );
  }

  return <AdminUserQuotaPage />;
}

function RestrictedArea({ description, title }: { description: string; title: string }) {
  return (
    <section className="rounded-[var(--radius-surface-4xl)] border border-border bg-card p-8 shadow-[0_8px_24px_rgba(15,23,42,0.03)]">
      <div className="max-w-xl space-y-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium tracking-[0.18em] text-amber-700 uppercase dark:bg-amber-500/15 dark:text-amber-300">
          <ShieldCheckIcon className="size-3.5" />
          Accès restreint
        </div>
        <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
        <p className="text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
    </section>
  );
}

function AdminCompaniesRoutePage() {
  const session = authClient.useSession().data;
  if (!session) return null;
  if (getUserRole(session) !== 'admin') {
    return (
      <RestrictedArea
        description="La gestion globale des entreprises est limitée aux administrateurs de la plateforme."
        title="Zone réservée aux administrateurs"
      />
    );
  }
  return <AdminCompaniesPage />;
}

function AdminCompanyUsageRoutePage() {
  const session = authClient.useSession().data;
  if (!session) return null;
  if (getUserRole(session) !== 'admin') {
    return (
      <RestrictedArea
        description="L’usage des entreprises est limité aux administrateurs de la plateforme."
        title="Zone réservée aux administrateurs"
      />
    );
  }
  return <AdminCompanyUsagePage />;
}

function CompanyMembersRoutePage() {
  const access = useOrganizationAccess();
  if (access.isPending) return <FullScreenLoader />;
  if (!access.canManageMembers) {
    return (
      <RestrictedArea
        description="La gestion des membres requiert la permission de lecture des membres dans l’entreprise active."
        title="Droits entreprise insuffisants"
      />
    );
  }
  return (
    <CompanyMembersPage
      createPath="/company/members/new"
      scope={activeCompanyMembersScope}
    />
  );
}

function CompanyMemberCreateRoutePage() {
  const access = useOrganizationAccess();
  if (access.isPending) return <FullScreenLoader />;
  if (!access.canManageMembers) {
    return (
      <RestrictedArea
        description="La création d’un membre requiert les droits d’administration de l’entreprise active."
        title="Droits entreprise insuffisants"
      />
    );
  }
  return (
    <CompanyMemberCreatePage
      listPath="/company/members"
      scope={activeCompanyMembersScope}
    />
  );
}

function AdminCompanyMembersRoutePage({ mode }: { mode: 'create' | 'list' }) {
  const session = authClient.useSession().data;
  const { companyId } = useParams<{ companyId: string }>();
  if (!session) return null;
  if (getUserRole(session) !== 'admin') {
    return (
      <RestrictedArea
        description="La gestion ciblée des membres est limitée aux administrateurs de la plateforme."
        title="Zone réservée aux administrateurs"
      />
    );
  }
  if (!companyId) {
    return (
      <RestrictedArea
        description="L’entreprise ciblée est invalide."
        title="Entreprise introuvable"
      />
    );
  }
  const listPath = `/admin/companies/${companyId}/members`;
  const scope = { companyId, type: 'targeted' } as const;
  return mode === 'create'
    ? <CompanyMemberCreatePage listPath={listPath} scope={scope} />
    : <CompanyMembersPage createPath={`${listPath}/new`} scope={scope} />;
}

function CompanySourcesRoutePage() {
  const access = useOrganizationAccess();
  if (access.isPending) return <FullScreenLoader />;
  if (!access.canManageDatasource) {
    return (
      <RestrictedArea
        description="La gestion des sources requiert la permission datasource:manage dans l’entreprise active."
        title="Droits entreprise insuffisants"
      />
    );
  }
  return (
    <Suspense fallback={<FullScreenLoader />}>
      <DatasourcePage />
    </Suspense>
  );
}

function SettingsRoutePage() {
  return <SettingsPage />;
}

function ChatRoutePage() {
  return <ChatPage />;
}

function ComponentsRoutePage() {
  return <DesignSystemPage />;
}

function DocumentsRoutePage() {
  return <DocumentsPage />;
}

function NotFoundPage() {
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <>
      <SentryUserScope />
      <SentryRoutes>
        <Route element={<PublicOnlyLayout />}>
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/set-password" element={<SetPasswordPage />} />
        </Route>
        <Route path="/" element={<HomePage />} />
        <Route element={<ProtectedLayout />}>
          <Route path="/chat" element={<ChatRoutePage />} />
          <Route path="/chat/rag" element={<Navigate to="/rag" replace />} />
          <Route path="/chat/:conversationId" element={<ChatRoutePage />} />
          <Route path="/rag" element={<ChatRoutePage />} />
          <Route path="/rag/:conversationId" element={<ChatRoutePage />} />
          <Route path="/settings" element={<SettingsRoutePage />} />
          <Route path="/account" element={<Navigate to="/settings" replace />} />
          <Route path="/usage" element={<Navigate to="/settings?tab=usage" replace />} />
          <Route path="/documents" element={<DocumentsRoutePage />} />
          <Route path="/components" element={<ComponentsRoutePage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/users/new" element={<AdminCreateUserPage />} />
          <Route path="/admin/users/:userId/quota" element={<AdminUserQuotaRoutePage />} />
          <Route path="/admin/companies" element={<AdminCompaniesRoutePage />} />
          <Route
            path="/admin/companies/:companyId/members"
            element={<AdminCompanyMembersRoutePage mode="list" />}
          />
          <Route
            path="/admin/companies/:companyId/members/new"
            element={<AdminCompanyMembersRoutePage mode="create" />}
          />
          <Route
            path="/admin/companies/:companyId/usage"
            element={<AdminCompanyUsageRoutePage />}
          />
          <Route path="/settings/assistant" element={<Navigate to="/settings?tab=general" replace />} />
          <Route path="/company/members" element={<CompanyMembersRoutePage />} />
          <Route path="/company/members/new" element={<CompanyMemberCreateRoutePage />} />
          <Route path="/company/sources" element={<CompanySourcesRoutePage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </SentryRoutes>
      <Toaster position="top-center" />
    </>
  );
}

export default App;
